import type { IPageState } from "@rhc-shared-components/table-toolbar";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { getProductScores } from "../api/pfs";
import { findProductListingsByNameQuery, getProductListingsQuery, getProjectListQuery } from "../api/pyxis";
import type { ProductScoreResponseMultiple } from "../constants/pfsContants";
import type { IPyxisError } from "../interfaces/common";
import type { IProductDetailWithCerts, IProductList } from "../interfaces/productList";
import type { IPaginationRequest } from "../modal/pagination";
import type { CertificationProject, ProductListingFilter, SortBy } from "../types/generated-graphql";
import { getOrgID } from "../utils/fetch";

// Base query key for invalidation
export const productListBaseQueryKey = ["productList"] as const;

export const productListQueryKey = (
	apiFilter: ProductListingFilter,
	searchValue: string,
	paginationState: IPaginationRequest,
	sortBy: SortBy[],
) => ["productList", apiFilter, searchValue, paginationState, sortBy] as const;

export const productScoreListQueryKey = (productIds: string[]) => ["productScoreList", productIds] as const;

export const useProductList = (
	apiFilter: ProductListingFilter,
	searchValue: string,
	paginationState: IPaginationRequest,
	sortBy: SortBy[] = [{ field: "last_update_date", order: "DESC" }],
): UseQueryResult<IProductList, IPyxisError> => {
	return useQuery<IProductList, IPyxisError>({
		queryKey: productListQueryKey(apiFilter, searchValue, paginationState, sortBy),
		queryFn: async ({ signal }) => {
			const { data: productListResponse } = searchValue
				? await findProductListingsByNameQuery(getOrgID(), paginationState, apiFilter, searchValue, sortBy, signal)
				: await getProductListingsQuery(getOrgID(), paginationState, apiFilter, sortBy, signal);
			if (productListResponse?.productListings?.data) {
				const attachedProjectsSet = new Set<string>();
				productListResponse?.productListings?.data.forEach((item) => {
					item?.cert_projects?.forEach((id: string) => {
						attachedProjectsSet.add(id);
					});
				});
				const projectPagination: IPageState = {
					page: 1,
					perPage: 250,
				};
				let recursive = true;
				const projectsData: Record<string, CertificationProject> = {};
				while (recursive) {
					const projectsListResponse = await getProjectListQuery(
						projectPagination,
						{
							and: [
								{
									_id: {
										in: Array.from(attachedProjectsSet),
									},
								},
								{
									project_status: {
										eq: "active",
									},
								},
							],
						},
						[{ field: "last_update_date", order: "DESC" }],
						signal,
					);
					// Recursively calling if total > 250
					const totalPages = Math.ceil(projectsListResponse?.data?.projectList?.total / projectPagination.perPage) || 1;
					recursive = totalPages < projectPagination.page;
					projectPagination.page = projectPagination.page + 1;
					projectsListResponse?.data?.projectList?.data?.forEach((item) => {
						if (item?._id) {
							projectsData[item._id] = item as CertificationProject;
						}
					});
				}
				// Saving to main product objects for easy access
				(productListResponse?.productListings?.data as IProductDetailWithCerts[])?.map(
					(item: IProductDetailWithCerts) => {
						const attachedCerts: CertificationProject[] = [];
						item?.cert_projects?.forEach((id: string) => {
							attachedCerts.push(projectsData[id]);
						});
						item.certDetails = attachedCerts?.filter((item) => item?.project_status === "active");
						return item;
					},
				);
			}
			return productListResponse?.productListings as IProductList;
		},
		placeholderData: (previousData) => previousData,
	}) as UseQueryResult<IProductList, IPyxisError>;
};

export const useProductScoreList = (
	productIds: string[],
	enabled: boolean = true,
): UseQueryResult<ProductScoreResponseMultiple[]> => {
	return useQuery<ProductScoreResponseMultiple[]>({
		queryKey: productScoreListQueryKey(productIds),
		queryFn: ({ signal }) => getProductScores(productIds, signal),
		placeholderData: (previousData) => previousData,
		enabled: enabled && productIds?.length > 0,
	});
};
